import Alpine from 'alpinejs'

import Swiper from 'swiper';
import { EffectCoverflow, Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-coverflow'; // <-- CRUCIAL: Don't forget this CSS import!
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const initSliderBlock = ($block) => {
    // $block is passed by ACF as a jQuery object
    const sliderContainer = $block[0].querySelector('.slider');
    if (!sliderContainer) return;

    // Find the wrapper (which is now our <InnerBlocks class="swiper-wrapper" />)
    const wrapper = sliderContainer.querySelector('.swiper-wrapper');

    if (wrapper) {
        // Automatically inject the 'swiper-slide' class to every block dropped inside
        Array.from(wrapper.children).forEach((child) => {
            child.classList.add('swiper-slide');
        });
    }

    // Initialize Swiper on the container
    new Swiper(sliderContainer, {
        // Pass the modules you want to use
        modules: [EffectCoverflow, Navigation, Pagination],
        
        // Enable Coverflow
        effect: 'coverflow',
        grabCursor: true,
        centeredSlides: true,
        slidesPerView: 'auto', // 'auto' works best so slides can stack side-by-side

        coverflowEffect: {
            rotate: 0,       // Slide rotation angle (in degrees)
            stretch: -100,       // Stretch space between slides (in px)
            depth: 140,       // Depth offset in 3D space (higher = further back)
            modifier: 2,      // Effect multiplier
            slideShadows: false, // Enables 3D ambient shadows on inactive slides
        },

        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
        },
    });
};

// Hook into the WordPress Block Editor execution loop
if (window.acf) {
    window.acf.addAction('render_block_preview/type=custom-group', initSliderBlock);
} else {
    // Live frontend website initialization
    document.addEventListener('DOMContentLoaded', () => {
        const liveSliders = document.querySelectorAll('.slider');
        liveSliders.forEach((slider) => {
            initSliderBlock([slider.parentElement]); 
        });
    });
}

document.addEventListener('alpine:init', () => {
    // We define the component and accept the Twig data as parameters
    Alpine.data('careerPathfinder', (skillsData, careersData) => ({
        step: 1,
        branchSelection: null,
        selectedSkills: [],
        selectedCareers: [],
        skills: skillsData,
        careers: careersData,
        
        // Handles Q1 clicks, branch clearing, and the smooth scroll
        selectBranch(branch) {
            this.branchSelection = branch;
            this.step = 2;
            
            if (branch === 'skills') {
                this.selectedCareers = [];
            } else {
                this.selectedSkills = [];
            }

            // Wait for the DOM to reveal Step 2, then scroll
            this.$nextTick(() => {
                document.getElementById('step-2-container')?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            });
        },

        // Keeps our HTML clean by handling the complex pill styling here
        getPillClasses(slug, type) {
            const isSelected = type === 'skills' 
                ? this.selectedSkills.includes(slug) 
                : this.selectedCareers.includes(slug);
                
            return isSelected 
                ? 'bg-[#24313B] text-white border-[#24313B] shadow-inner' 
                : 'bg-white/90 text-[#333333] border-transparent hover:bg-white hover:shadow-md';
        },

        // Evaluates if the submit button should be active
        isSubmitDisabled() {
            if (this.branchSelection === 'skills') return this.selectedSkills.length === 0;
            if (this.branchSelection === 'career') return this.selectedCareers.length === 0;
            return true;
        },

        // Handles analytics tracking and form submission
        submitForm() {
            if (this.isSubmitDisabled()) return;

            // 1. Fire Fathom Event
            if (window.fathom) {
                // Standard Fathom event tracking: trackEvent('Event Name', { _value: cents })
                window.fathom.trackEvent('Explore pathways Form Submitted');

                // Optional: Send custom parameters or track specific branch choices

                if (this.branchSelection === 'skills') {
                    window.fathom.trackEvent(`Explore pathways by skills: ${this.selectedSkills}`);
                } else {
                    window.fathom.trackEvent(`Explore pathways by career: ${this.selectedCareers}`);
                }
                
            }

            // 2. Submit the form element using $el
            // $el references the HTML form element bound to x-data
            this.$el.submit();
        }
    })); // <-- THIS closes the Alpine.data object and function


    Alpine.data('careerDirectory', (initialSkills, initialCareers, allCareers) => ({
        // State
        selectedSkills: initialSkills || [],
        selectedCareers: initialCareers || [],
        careers: allCareers || [],

        // Computed property: returns only the careers that match the active filters
        get filteredCareers() {
            return this.careers.filter(career => {
                
                // If no skills are selected, it passes. Otherwise, it must have at least one selected skill.
                const matchesSkills = this.selectedSkills.length === 0 || 
                    this.selectedSkills.some(skillSlug => career.skills.includes(skillSlug));
                
                // Same logic for career areas
                const matchesCareerAreas = this.selectedCareers.length === 0 || 
                    this.selectedCareers.some(careerSlug => career.career_areas.includes(careerSlug));

                // The career must match BOTH active filter groups to show up
                return matchesSkills && matchesCareerAreas;
            });
        },

        // Helper to quickly clear filters
        clearFilters() {
            this.selectedSkills = [];
            this.selectedCareers = [];
        }
    }));

    Alpine.data('tocData', () => ({
        headings: [],

        init() {
            // Find the headings
            const domHeadings = document.querySelectorAll('.entry-content h2[id], .entry-content h3[id]');
            
            // Convert the NodeList into an array of objects for Alpine to use
            this.headings = Array.from(domHeadings).map(heading => ({
                id: heading.id,
                text: heading.textContent,
                level: heading.tagName.toLowerCase()
            }));
        }
    }));

    // Create the observer
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            // If the heading scrolls into view
            if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                
                // Stop observing it so the animation only happens once
                observer.unobserve(entry.target); 
            }
        });
    }, { 
        threshold: 0.5 // Triggers when 50% of the heading is visible
    });

    // Find all WP block headings with our class and observe them
    const headings = document.querySelectorAll('.is-style-left-line');
    headings.forEach(heading => {
        observer.observe(heading);
    });

});

window.Alpine = Alpine
Alpine.start()

